import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaShieldAlt, FaHandshake, FaChartLine, FaClock,
  FaUsers, FaAward, FaArrowRight
} from 'react-icons/fa';

const stats = [
  { value: '12+', label: 'Years of Excellence' },
  { value: '5000+', label: 'Clients Served' },
  { value: '10+', label: 'Services Offered' },
  { value: '99%', label: 'Client Satisfaction' },
];

const values = [
  { icon: FaShieldAlt, title: 'Integrity', desc: 'We operate with complete transparency and honesty in every client relationship.' },
  { icon: FaHandshake, title: 'Trust', desc: 'Building long-lasting partnerships grounded in mutual trust and respect.' },
  { icon: FaChartLine, title: 'Excellence', desc: 'Delivering the highest quality tax and accounting services for optimal outcomes.' },
  { icon: FaClock, title: 'Reliability', desc: 'Always available and responsive, ensuring your financial needs are met on time.' },
  { icon: FaUsers, title: 'Client-First', desc: 'Your growth and satisfaction are at the core of everything we do.' },
  { icon: FaAward, title: 'Expertise', desc: 'CPA-certified professionals with deep knowledge of Canadian financial law.' },
];

export default function About() {
  React.useEffect(() => {
    document.title = "OrientalPay | About Us";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute('content', 'Learn about Oriental Business Solutions — a CPA-led accounting and tax firm serving individuals and businesses across Canada.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        {/* Background image */}
        <img
          src="/About us.png"
          alt="About Oriental Business Solutions"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1e45] via-[#0d1e45]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d1e45]/80 via-transparent to-transparent" />

        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 pb-20 pt-40 w-full">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-300 text-xs font-bold uppercase tracking-widest mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
            About Us
          </span>
          <h1 className="text-5xl md:text-7xl font-black text-white leading-[1.05] mb-6 max-w-3xl">
            We Are Your<br />
            <span className="text-transparent bg-clip-text"
              style={{ backgroundImage: 'linear-gradient(90deg, #ffbe1a, #ffd966)' }}>
              Trusted Partners
            </span>
          </h1>
          <p className="text-blue-100 text-base md:text-lg font-medium leading-relaxed max-w-xl">
            Oriental Business Solutions has been helping Canadians navigate taxes, bookkeeping, and financial planning for over 12 years — with care, expertise, and results.
          </p>
        </div>
      </section>

      {/* ── STATS STRIP ──────────────────────────────────────── */}
      <section className="bg-[#0d1e45] py-0">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {stats.map((s, i) => (
              <div key={i} className="py-10 px-6 text-center">
                <div className="text-4xl md:text-5xl font-black text-amber-400 mb-1">{s.value}</div>
                <div className="text-blue-200 text-xs font-semibold uppercase tracking-widest">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHO WE ARE ───────────────────────────────────────── */}
      <section className="py-24 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-[#3b5eb4] text-xs font-bold uppercase tracking-widest mb-3 block">Our Story</span>
          <h2 className="text-4xl md:text-5xl font-black text-[#0d1e45] leading-tight mb-8">
            Who We Are
          </h2>
          <div className="space-y-6 text-gray-600 text-base font-medium leading-relaxed mb-10 text-justify md:text-center">
            <p>
              Oriental Business Solutions Inc. was founded on the belief that every individual and business deserves expert financial guidance — regardless of size or complexity. We are a young and dynamic company focused on customer satisfaction, building long-lasting partnerships grounded in mutual trust and respect.
            </p>
            <p>
              Starting as a tax preparation and planning office, we have grown to offer a comprehensive range of services including tax, insurance, investments, business advisory, incorporation, and mortgages.
            </p>
            <p>
              Whether you are a small business owner, an international student, an independent contractor, or someone seeking personal tax help — we are here to save your money and maximize your returns.
            </p>
          </div>
          <Link to="/contact">
            <button className="inline-flex items-center gap-2 px-7 py-3.5 btn-payday-blue text-sm font-bold shadow-lg">
              Get in Touch <FaArrowRight className="text-xs" />
            </button>
          </Link>
        </div>
      </section>

      {/* ── FOUNDER SECTION ──────────────────────────────────── */}
      <section className="py-24 px-6 md:px-12"
        style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #e8eeff 100%)' }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-[#3b5eb4] text-xs font-bold uppercase tracking-widest mb-3 block">Leadership</span>
            <h2 className="text-4xl md:text-5xl font-black text-[#0d1e45]">Meet Our Founder</h2>
          </div>

          <div className="bg-white rounded-[2rem] shadow-xl overflow-hidden p-8 md:p-14 border border-blue-100">
            {/* Header info with Initials Avatar */}
            <div className="flex flex-col sm:flex-row items-center gap-6 mb-8 pb-8 border-b border-gray-100">
              <div className="w-20 h-20 rounded-full bg-gradient-to-tr from-[#0d1e45] to-[#3b5eb4] flex items-center justify-center text-white text-2xl font-black shadow-md shrink-0">
                AM
              </div>
              <div className="text-center sm:text-left">
                <h3 className="text-2xl font-black text-[#0d1e45]">Ashok Manickam</h3>
                <p className="text-amber-500 text-sm font-bold uppercase tracking-wider">CPA, CGA — Founder</p>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[#e8eeff] flex items-center justify-center text-[#3b5eb4]">
                <FaAward />
              </div>
              <div>
                <div className="text-xs font-bold text-gray-400 uppercase tracking-widest">Chartered Professional Accountant</div>
                <div className="text-sm font-extrabold text-[#0d1e45]">Certified Since 2011</div>
              </div>
            </div>

            <div className="space-y-4 text-gray-500 text-sm font-medium leading-relaxed mb-8">
              <p>
                <strong className="text-[#0d1e45] font-extrabold">Ashok Manickam</strong> became a Certified General Accountant (CGA) in 2011 and, with the unification of the accounting profession in Canada, is now a <strong className="text-[#3b5eb4]">Chartered Professional Accountant (CPA)</strong>.
              </p>
              <p>
                He is well-rounded in Personal Taxes, Corporate Taxes, Bookkeeping, and preparing year-end financial statements. His knowledge and understanding of the Canadian financial market has been highly beneficial in assessing clients' needs — whether personal or corporate — for over 12 years.
              </p>
              <p>
                He also provides bookkeeping support to clients that perform all accounting functions in-house, and is dedicated, hard-working, and well-liked by clients and colleagues alike.
              </p>
            </div>

            <blockquote className="border-l-4 border-amber-400 pl-5 italic text-[#3b5eb4] text-sm font-bold">
              "The right advice from our tax experts will save you time and money!"
            </blockquote>
          </div>
        </div>
      </section>

      {/* ── MISSION / VISION ─────────────────────────────────── */}
      <section className="py-24 px-6 md:px-12 bg-white">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
          {/* Mission */}
          <div className="rounded-3xl p-10 border border-gray-100 shadow-lg hover:shadow-xl transition-shadow relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-5 group-hover:opacity-10 transition-opacity"
              style={{ background: 'radial-gradient(circle, #3b5eb4, transparent)', transform: 'translate(30%, -30%)' }} />
            <div className="w-12 h-12 rounded-2xl bg-[#e8eeff] flex items-center justify-center text-[#3b5eb4] text-xl mb-6">
              <FaChartLine />
            </div>
            <h3 className="text-2xl font-black text-[#0d1e45] mb-4">Our Mission</h3>
            <p className="text-gray-500 text-sm font-medium leading-relaxed">
              To empower our clients to achieve financial success through proactive and strategic tax planning. We strive to exceed expectations by delivering innovative solutions, personalized service, and unparalleled expertise.
            </p>
          </div>
          {/* Vision */}
          <div className="rounded-3xl p-10 border border-gray-100 shadow-lg hover:shadow-xl transition-shadow relative overflow-hidden group"
            style={{ background: 'linear-gradient(135deg, #0d1e45 0%, #1a3268 100%)' }}>
            <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-10 group-hover:opacity-20 transition-opacity"
              style={{ background: 'radial-gradient(circle, #ffbe1a, transparent)', transform: 'translate(30%, -30%)' }} />
            <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center text-amber-400 text-xl mb-6">
              <FaAward />
            </div>
            <h3 className="text-2xl font-black text-white mb-4">Our Vision</h3>
            <p className="text-blue-200 text-sm font-medium leading-relaxed">
              To be the leading tax consulting firm known for integrity, professionalism, and client satisfaction. We aim to set the standard for excellence, continually adapting to meet the evolving needs of our clients and the regulatory environment.
            </p>
          </div>
        </div>
      </section>

      {/* ── OUR VALUES ───────────────────────────────────────── */}
      <section className="py-24 px-6 md:px-12 bg-gray-50 border-t border-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-[#3b5eb4] text-xs font-bold uppercase tracking-widest mb-3 block">What We Stand For</span>
            <h2 className="text-4xl md:text-5xl font-black text-[#0d1e45]">Our Core Values</h2>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
            {values.map((v, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 shadow-sm hover:shadow-lg border border-gray-100 transition-all duration-300 group cursor-default">
                <div className="w-11 h-11 rounded-xl bg-[#e8eeff] flex items-center justify-center text-[#3b5eb4] text-lg mb-5 group-hover:bg-[#3b5eb4] group-hover:text-white transition-all duration-300">
                  <v.icon />
                </div>
                <h4 className="text-base font-extrabold text-[#0d1e45] mb-2">{v.title}</h4>
                <p className="text-gray-500 text-xs font-medium leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────── */}
      <section className="py-20 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-black text-[#0d1e45] mb-4">
            Ready to get started?
          </h2>
          <p className="text-gray-500 text-sm font-medium mb-8 max-w-xl mx-auto leading-relaxed">
            Contact our team today and let us put our expertise to work for your financial future.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact">
              <button className="px-8 py-3.5 btn-payday-blue text-sm font-bold inline-flex items-center gap-2">
                Book a Free Consultation <FaArrowRight className="text-xs" />
              </button>
            </Link>
            <Link to="/products">
              <button className="px-8 py-3.5 rounded-full border-2 border-[#0d1e45] text-[#0d1e45] text-sm font-bold hover:bg-[#0d1e45] hover:text-white transition-all duration-200">
                View Our Services
              </button>
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}
